import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";


import {
  FiChevronRight,
  FiChevronDown,
  FiPlus,
  FiBox,
  FiPhone,
  FiSettings,
  FiHome,
  FiAlertTriangle,
  FiLogOut,
  FiGlobe,
  FiClipboard,
  FiFileText,
  FiUsers,
  FiBriefcase,
} from "react-icons/fi";
import { canAccessPath } from "../auth/permissions";
import useAccessPolicyVersion from "../auth/useAccessPolicyVersion";

/**
 * =========================
 * BRAND IP DEPOT
 * =========================
 * - Navy (swatch): #0B1120
 * - Azul logo aprox: #24307A (RGB 36,48,122)
 *
 *  Pon tu logo en /public/brand/ipdepot-logo.png
 * y deja LOGO_SRC asi­. Si lo tienes en otro lado, cambia la ruta.
 */
const LOGO_SRC = "/brand/ipdepot-logo.png"; // <-- CAMBIA SOLO ESTO SI ES NECESARIO

const BRAND = {
  navy: "#0B1120",
  primary: "#24307A",
  primaryRgb: "36,48,122",
};

export default function MenuLayout({ children }) {
  const location = useLocation();
  useAccessPolicyVersion();

  const [openAgregar, setOpenAgregar] = useState(false);
  const [openReportes, setOpenReportes] = useState(false);
  const [openIncidencias, setOpenIncidencias] = useState(false);
  const [openDocumentos, setOpenDocumentos] = useState(false);
  const [openInventario, setOpenInventario] = useState(false);
  const [openCompras, setOpenCompras] = useState(false);

  //  PRO: sidebar colapsable (no rompe nada)
  const [collapsed, setCollapsed] = useState(false);

  //  Badges listos para conectar a tu API cuando quieras
  const [badges, setBadges] = useState({
    incidencias: 0,
    evidencias: 0,
    documentos: 0,
    reportes: 0,
  });

  const isHome = location.pathname === "/";
  const canInicio = canAccessPath("/");

  const canAltaEquipo =
    canAccessPath("/agregar");

  const canAltaServicio =
    canAccessPath("/contratos/agregar");

  const canAltaCredencial =
    canAccessPath("/sitios/nuevo");

  const canAgregar =
    canAltaEquipo ||
    canAltaServicio ||
    canAltaCredencial;

  const canInventario =
    canAccessPath("/inventario");

  const canProyecto =
    canAccessPath("/proyectos/command");

  const canCotizaciones =
    canAccessPath("/cotizaciones");

  const canInsumosProduccion =
    canAccessPath("/insumos-produccion");

  const canControlFolios =
    canAccessPath("/folios");

  const canRemisiones =
    canAccessPath("/remisiones-envio");

  const canCompras =
    canCotizaciones ||
    canInsumosProduccion ||
    canControlFolios ||
    canRemisiones;

  const canReportarIncidencia =
    canAccessPath("/incidencias/crear");

  const canEvidencias =
    canAccessPath("/evidencias");

  const canIncidencias =
    canReportarIncidencia ||
    canEvidencias;

  const canServicios =
    canAccessPath("/contratos");

  const canPersonal =
    canAccessPath("/empleados");

  const canSitios =
    canAccessPath("/sitios");

  const canDocumentoCarga =
    canAccessPath("/documentos/carga");

  const canDocumentoArchivo =
    canAccessPath("/documentos/archivo");

  const canDocumentoBuscar =
    canAccessPath("/documentos/buscar");

  const canDocumentos =
    canDocumentoCarga ||
    canDocumentoArchivo ||
    canDocumentoBuscar;

  const canSeguridad =
    canAccessPath("/configuracion/accesos");

  const canConfiguracion =
    canAccessPath("/configuracion");

  const canOperacionSection =
    canInicio ||
    canAgregar ||
    canInventario ||
    canProyecto ||
    canCompras ||
    canIncidencias;

  const canGestionSection =
    canServicios ||
    canPersonal ||
    canSitios ||
    canDocumentos ||
    canSeguridad ||
    canConfiguracion;

  //  Auto-abrir menus cuando estas dentro
  useEffect(() => {
    if (location.pathname.startsWith("/inventario")) setOpenInventario(true);

    if (
      canCompras &&
      (
        location.pathname.startsWith("/cotizaciones") ||
        location.pathname.startsWith("/insumos-produccion") ||
        location.pathname.startsWith("/folios") ||
        location.pathname.startsWith("/remisiones-envio")
      )
    ) {
      setOpenCompras(true);
    }

    if (
      location.pathname.startsWith("/agregar") ||
      location.pathname.startsWith("/contratos/agregar") ||
      location.pathname.startsWith("/sitios/nuevo")
    ) {
      setOpenAgregar(true);
    }

    if (location.pathname.startsWith("/reportes")) setOpenReportes(true);

    if (
      location.pathname.startsWith("/incidencias") ||
      location.pathname.startsWith("/evidencias")
    ) {
      setOpenIncidencias(true);
    }

    if (location.pathname.startsWith("/documentos")) setOpenDocumentos(true);
  }, [location.pathname]);

  //  (Opcional) aqui­ conectas conteos reales:
  useEffect(() => {
    // Ejemplo:
    // setBadges({ incidencias: 3, evidencias: 1, documentos: 2, reportes: 0 });
  }, []);

  const sectionChip = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    return (parts[0] || "inicio").toUpperCase();
  }, [location.pathname]);

  const agregarActive = isAgregarActive(location.pathname);
  const incidenciasActive =
    location.pathname.startsWith("/incidencias") ||
    location.pathname.startsWith("/evidencias");

  const documentosActive = location.pathname.startsWith("/documentos");
  const reportesActive = location.pathname.startsWith("/reportes");
  const inventarioActive = location.pathname.startsWith("/inventario");
  const comprasActive =
    location.pathname.startsWith("/cotizaciones") ||
    location.pathname.startsWith("/insumos-produccion") ||
    location.pathname.startsWith("/remisiones-envio");

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <style>{css(BRAND)}</style>

      {/* SIDEBAR */}
      <aside
        className="ip-sidebar"
        style={{
          ...sx.sidebarShell(BRAND),
          width: collapsed ? 92 : 300,
          transition: "width .18s ease",
        }}
      >
        {/* BRAND */}
        <div style={sx.brandOuter}>
          <div
            className="ip-card"
            style={{
              ...sx.brandCard,
              padding: collapsed ? "12px 10px" : "12px 14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* Logo */}
              <div style={sx.logoWrap(collapsed, BRAND)}>
                <img
                  src={LOGO_SRC}
                  alt="IP DEPOT"
                  style={sx.logoImg(collapsed)}
                  onError={(e) => {
                    // fallback silencioso si no encuentra el logo
                    e.currentTarget.style.display = "none";
                  }}
                />
                {/* fallback mini si no carga el logo */}
                <div style={sx.logoFallback(collapsed, BRAND)}>IP</div>
              </div>

              {!collapsed && (
                <div style={{ minWidth: 0 }}>
                  <div style={sx.brandKicker}>IP DEPOT</div>
                  <div style={sx.brandName}>Sistema Inventario</div>
                  <div style={sx.brandMetaRow}>
                    <span style={sx.pill(BRAND)}>Prod</span>
                    <span style={sx.dot} />
                    <span style={sx.metaText}>/{sectionChip.toLowerCase()}</span>
                  </div>
                </div>
              )}

              <div style={{ marginLeft: "auto" }}>
                <button
                  type="button"
                  onClick={() => setCollapsed((v) => !v)}
                  className="ip-iconbtn"
                  style={sx.collapseBtn}
                  title={collapsed ? "Expandir menu" : "Colapsar menu"}
                  aria-label={collapsed ? "Expandir menu" : "Colapsar menu"}
                >
                  {collapsed ? ">>" : "<<"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* NAV SCROLL */}
        <div className="ip-navscroll" style={sx.navScroll}>
          {!collapsed && canOperacionSection && (
            <>
              <div style={sx.sectionLabel}>Operación</div>
              <Divider />
            </>
          )}

          {canInicio && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiHome size={18} />}
              to="/"
              text="Inicio"
              active={location.pathname === "/"}
              brand={BRAND}
            />
          )}

          {canAgregar && (
            <div>
              <button
                type="button"
                onClick={() =>
                  setOpenAgregar((value) => !value)
                }
                className={cx(
                  "ip-menuhead",
                  (openAgregar || agregarActive) &&
                    "is-active"
                )}
                style={sx.menuHeader(collapsed)}
                title={
                  collapsed
                    ? "Altas Operativas"
                    : ""
                }
              >
                <div style={sx.rowLeft}>
                  <span
                    className={cx(
                      "ip-iconbox",
                      (openAgregar ||
                        agregarActive) &&
                        "is-active"
                    )}
                  >
                    <FiPlus size={18} />
                  </span>

                  {!collapsed && (
                    <span style={sx.itemText}>
                      Altas Operativas
                    </span>
                  )}
                </div>

                {!collapsed && (
                  <div style={sx.rightMeta}>
                    <span style={sx.caret}>
                      {openAgregar
                        ? <FiChevronDown />
                        : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </button>

              {!collapsed && openAgregar && (
                <div
                  className="ip-submenu"
                  style={sx.submenu}
                >
                  {canAltaEquipo && (
                    <AwsSubItem
                      to="/agregar"
                      text="Alta de Equipo"
                      active={location.pathname.startsWith(
                        "/agregar"
                      )}
                    />
                  )}

                  {canAltaServicio && (
                    <AwsSubItem
                      to="/contratos/agregar"
                      text="Alta de Servicio"
                      active={location.pathname.startsWith(
                        "/contratos/agregar"
                      )}
                    />
                  )}

                  {canAltaCredencial && (
                    <AwsSubItem
                      to="/sitios/nuevo"
                      text="Alta de Credencial"
                      active={location.pathname.startsWith(
                        "/sitios/nuevo"
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {canInventario && (
            <div>
              <button
                type="button"
                onClick={() =>
                  setOpenInventario((value) => !value)
                }
                className={cx(
                  "ip-menuhead",
                  (openInventario ||
                    inventarioActive) &&
                    "is-active"
                )}
                style={sx.menuHeader(collapsed)}
                title={
                  collapsed
                    ? "Parque Tecnológico"
                    : ""
                }
              >
                <div style={sx.rowLeft}>
                  <span
                    className={cx(
                      "ip-iconbox",
                      (openInventario ||
                        inventarioActive) &&
                        "is-active"
                    )}
                  >
                    <FiClipboard size={18} />
                  </span>

                  {!collapsed && (
                    <span style={sx.itemText}>
                      Parque Tecnológico
                    </span>
                  )}
                </div>

                {!collapsed && (
                  <div style={sx.rightMeta}>
                    <span style={sx.caret}>
                      {openInventario
                        ? <FiChevronDown />
                        : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </button>

              {!collapsed && openInventario && (
                <div
                  className="ip-submenu"
                  style={sx.submenu}
                >
                  <AwsSubItem
                    to="/inventario"
                    text="Inventario de Equipos"
                    active={
                      location.pathname ===
                      "/inventario"
                    }
                  />
                </div>
              )}
            </div>
          )}

          {canProyecto && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiBriefcase size={18} />}
              to="/proyectos/command"
              text="Gestión de Proyectos"
              active={location.pathname.startsWith(
                "/proyectos/command"
              )}
              brand={BRAND}
            />
          )}

          {canCompras && (
            <div>
              <button
                type="button"
                onClick={() =>
                  setOpenCompras((value) => !value)
                }
                className={cx(
                  "ip-menuhead",
                  (openCompras || comprasActive) &&
                    "is-active"
                )}
                style={sx.menuHeader(collapsed)}
                title={
                  collapsed
                    ? "Proveedores y Compras"
                    : ""
                }
              >
                <div style={sx.rowLeft}>
                  <span
                    className={cx(
                      "ip-iconbox",
                      (openCompras ||
                        comprasActive) &&
                        "is-active"
                    )}
                  >
                    <FiClipboard size={18} />
                  </span>

                  {!collapsed && (
                    <span style={sx.itemText}>
                      Proveedores y Compras
                    </span>
                  )}
                </div>

                {!collapsed && (
                  <div style={sx.rightMeta}>
                    <span style={sx.caret}>
                      {openCompras
                        ? <FiChevronDown />
                        : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </button>

              {!collapsed && openCompras && (
                <div
                  className="ip-submenu"
                  style={sx.submenu}
                >
                  {canCotizaciones && (
                    <AwsSubItem
                      to="/cotizaciones"
                      text="Cotizaciones"
                      active={location.pathname.startsWith(
                        "/cotizaciones"
                      )}
                    />
                  )}

                  {canInsumosProduccion && (
                    <AwsSubItem
                      to="/insumos-produccion"
                      text="Insumos de Producción"
                      active={location.pathname.startsWith(
                        "/insumos-produccion"
                      )}
                    />
                  )}

                  {canControlFolios && (
                    <AwsSubItem
                      to="/folios"
                      text="Control de Folios"
                      active={location.pathname.startsWith(
                        "/folios"
                      )}
                    />
                  )}

                  {canRemisiones && (
                    <AwsSubItem
                      to="/remisiones-envio"
                      text="Remisiones de Envío"
                      active={location.pathname.startsWith(
                        "/remisiones-envio"
                      )}
                    />
                  )}
                   {canAccessPath("/configuracion/accesos") && (
                     <AwsSubItem
                       to="/acuses-control"
                       text="Control de Acuses"
                       active={location.pathname.startsWith(
                         "/acuses-control"
                       )}
                     />
                   )}
                </div>
              )}
            </div>
          )}

          {canIncidencias && (
            <div>
              <button
                type="button"
                onClick={() =>
                  setOpenIncidencias((value) => !value)
                }
                className={cx(
                  "ip-menuhead",
                  (openIncidencias ||
                    incidenciasActive) &&
                    "is-active"
                )}
                style={sx.menuHeader(collapsed)}
                title={
                  collapsed
                    ? "Soporte Operativo"
                    : ""
                }
              >
                <div style={sx.rowLeft}>
                  <span
                    className={cx(
                      "ip-iconbox",
                      (openIncidencias ||
                        incidenciasActive) &&
                        "is-active"
                    )}
                  >
                    <FiAlertTriangle size={18} />
                  </span>

                  {!collapsed && (
                    <span style={sx.itemText}>
                      Soporte Operativo
                    </span>
                  )}
                </div>

                {!collapsed && (
                  <div style={sx.rightMeta}>
                    <Badge
                      value={
                        badges.incidencias +
                        badges.evidencias
                      }
                      tone="warn"
                      brand={BRAND}
                    />

                    <span style={sx.caret}>
                      {openIncidencias
                        ? <FiChevronDown />
                        : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </button>

              {!collapsed && openIncidencias && (
                <div
                  className="ip-submenu"
                  style={sx.submenu}
                >
                  {canReportarIncidencia && (
                    <AwsSubItem
                      to="/incidencias/crear"
                      text="Reportar Incidencia"
                      active={location.pathname.startsWith(
                        "/incidencias/crear"
                      )}
                    />
                  )}

                  {canEvidencias && (
                    <AwsSubItem
                      to="/evidencias"
                      text="Evidencias Operativas"
                      active={location.pathname.startsWith(
                        "/evidencias"
                      )}
                      right={
                        <Badge
                          value={badges.evidencias}
                          tone="warn"
                          brand={BRAND}
                        />
                      }
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {!collapsed && canGestionSection && (
            <>
              <div style={sx.sectionLabel}>Gestión</div>
              <Divider />
            </>
          )}

          {canServicios && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiPhone size={18} />}
              to="/contratos"
              text="Servicios y Suscripciones"
              active={location.pathname.startsWith(
                "/contratos"
              )}
              brand={BRAND}
            />
          )}

          {canPersonal && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiUsers size={18} />}
              to="/empleados"
              text="Directorio de Personal"
              active={location.pathname.startsWith(
                "/empleados"
              )}
              brand={BRAND}
            />
          )}

          {canSitios && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiGlobe size={18} />}
              to="/sitios"
              text="Credenciales de Acceso"
              active={location.pathname.startsWith(
                "/sitios"
              )}
              brand={BRAND}
            />
          )}

          {canDocumentos && (
            <div>
              <button
                type="button"
                onClick={() =>
                  setOpenDocumentos((value) => !value)
                }
                className={cx(
                  "ip-menuhead",
                  (openDocumentos ||
                    documentosActive) &&
                    "is-active"
                )}
                style={sx.menuHeader(collapsed)}
                title={
                  collapsed
                    ? "Gestión Documental"
                    : ""
                }
              >
                <div style={sx.rowLeft}>
                  <span
                    className={cx(
                      "ip-iconbox",
                      (openDocumentos ||
                        documentosActive) &&
                        "is-active"
                    )}
                  >
                    <FiFileText size={18} />
                  </span>

                  {!collapsed && (
                    <span style={sx.itemText}>
                      Gestión Documental
                    </span>
                  )}
                </div>

                {!collapsed && (
                  <div style={sx.rightMeta}>
                    <Badge
                      value={badges.documentos}
                      tone="info"
                      brand={BRAND}
                    />

                    <span style={sx.caret}>
                      {openDocumentos
                        ? <FiChevronDown />
                        : <FiChevronRight />}
                    </span>
                  </div>
                )}
              </button>

              {!collapsed && openDocumentos && (
                <div
                  className="ip-submenu"
                  style={sx.submenu}
                >
                  {canDocumentoCarga && (
                    <AwsSubItem
                      to="/documentos/carga"
                      text="Carga Documental"
                      active={location.pathname.startsWith(
                        "/documentos/carga"
                      )}
                    />
                  )}

                  {canDocumentoArchivo && (
                    <AwsSubItem
                      to="/documentos/archivo"
                      text="Archivo Documental"
                      active={location.pathname.startsWith(
                        "/documentos/archivo"
                      )}
                    />
                  )}

                  {canDocumentoBuscar && (
                    <AwsSubItem
                      to="/documentos/buscar"
                      text="Búsqueda OCR"
                      active={location.pathname.startsWith(
                        "/documentos/buscar"
                      )}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {canSeguridad && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiSettings size={18} />}
              to="/configuracion/accesos"
              text="Seguridad y Accesos"
              active={location.pathname.startsWith(
                "/configuracion/accesos"
              )}
              brand={BRAND}
            />
          )}

          {canConfiguracion && (
            <AwsItem
              collapsed={collapsed}
              icon={<FiSettings size={18} />}
              to="/configuracion"
              text="Configuración"
              active={
                location.pathname ===
                "/configuracion"
              }
              brand={BRAND}
            />
          )}
        </div>

        {/* FOOTER */}
        <div style={sx.footer(collapsed)}>
          <button
            onClick={() => (window.location.href = "/logout")}
            className="ip-logout"
            style={sx.logoutBtn(BRAND)}
            title={collapsed ? "Cerrar sesion" : ""}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              <FiLogOut />
              {!collapsed && "Cerrar sesion"}
            </span>
          </button>

          {!collapsed && <div style={sx.footerHint}>Tecnologia - Seguridad - Innovacion</div>}
        </div>
      </aside>

      {/* CONTENIDO */}
      <main
        style={{
          flex: 1,
          height: "100vh",
          overflowY: location.pathname === "/" ? "hidden" : "auto",
          background: "#f8fafc",
          padding: isHome ? 0 : 30,
        }}
      >
        {children || <Outlet />}
      </main>
    </div>
  );
}

/* =========================
   HELPERS
========================= */
function isAgregarActive(pathname) {
  return (
    pathname.startsWith("/agregar") ||
    pathname.startsWith("/contratos/agregar") ||
    pathname.startsWith("/sitios/nuevo")
  );
}
function cx(...a) {
  return a.filter(Boolean).join(" ");
}

/* =========================
   UI PIECES
========================= */
function Divider() {
  return <div style={sx.divider} />;
}

function Badge({ value = 0, tone = "neutral", brand }) {
  const v = Number(value) || 0;
  if (v <= 0) return null;

  const style = {
    ...sx.badgeBase,
    ...(tone === "warn"
      ? sx.badgeWarn
      : tone === "info"
      ? sx.badgeInfo(brand)
      : sx.badgeNeutral),
  };

  return <span style={style}>{v > 99 ? "99+" : v}</span>;
}

/* =========================
   COMPONENTES
========================= */
function AwsItem({ icon, text, to, active, collapsed, brand }) {
  if (!canAccessPath(to)) return null;

  return (
    <Link
      to={to}
      className={cx("ip-item", active && "is-active")}
      style={sx.item(collapsed)}
      title={collapsed ? text : ""}
    >
      <span className={cx("ip-iconbox", active && "is-active")}>{icon}</span>
      {!collapsed && <span style={sx.itemText}>{text}</span>}
      {!collapsed && active && <span style={sx.activePill(brand)}>ACTIVO</span>}
    </Link>
  );
}

function AwsSubItem({ to, text, active, right = null }) {
  if (!canAccessPath(to)) return null;

  return (
    <Link to={to} className={cx("ip-subitem", active && "is-active")} style={sx.subItem}>
      <span className={cx("ip-subdot", active && "is-active")} />
      <span style={{ flex: 1 }}>{text}</span>
      {right}
    </Link>
  );
}

/* =========================
   STYLES
========================= */
const sx = {
  sidebarShell: (brand) => ({
    background:
      `radial-gradient(1200px 520px at 0% 0%, rgba(${brand.primaryRgb},0.18), transparent 55%),` +
      `linear-gradient(180deg, ${brand.navy} 0%, #0A1324 55%, #09101E 100%)`,
    color: "#e5e7eb",
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid rgba(255,255,255,0.08)",
  }),

  brandOuter: { paddingTop: 14 },

  brandCard: {
    margin: "0 10px 10px",
    borderRadius: 18,
    background: "rgba(255,255,255,0.045)",
    border: "1px solid rgba(255,255,255,0.07)",
    overflow: "hidden",
  },

  logoWrap: (collapsed, brand) => ({
    width: collapsed ? 48 : 52,
    height: collapsed ? 48 : 52,
    borderRadius: 16,
    background: `rgba(${brand.primaryRgb},0.14)`,
    border: `1px solid rgba(${brand.primaryRgb},0.28)`,
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    flex: "0 0 auto",
  }),

  logoImg: (collapsed) => ({
    width: collapsed ? 36 : 44,
    height: "auto",
    objectFit: "contain",
    display: "block",
    filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.25))",
  }),

  logoFallback: (collapsed, brand) => ({
    width: collapsed ? 36 : 44,
    height: collapsed ? 36 : 44,
    borderRadius: 14,
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    color: "#fff",
    background: `rgba(${brand.primaryRgb},0.22)`,
    letterSpacing: "-0.02em",
  }),

  brandKicker: {
    fontSize: 11,
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "rgba(148,163,184,0.92)",
    fontWeight: 900,
  },
  brandName: {
    marginTop: 6,
    fontSize: 16,
    fontWeight: 900,
    color: "#fff",
    letterSpacing: "-0.02em",
  },
  brandMetaRow: {
    marginTop: 10,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  pill: (brand) => ({
    fontSize: 11,
    fontWeight: 900,
    padding: "3px 10px",
    borderRadius: 999,
    background: `rgba(${brand.primaryRgb},0.14)`,
    border: `1px solid rgba(${brand.primaryRgb},0.26)`,
    color: "rgba(255,255,255,0.92)",
  }),
  dot: { width: 4, height: 4, borderRadius: 999, background: "rgba(148,163,184,0.78)" },
  metaText: {
    fontSize: 12,
    color: "rgba(148,163,184,0.92)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
  },

  collapseBtn: {
    width: 34,
    height: 28,
    borderRadius: 10,
  },

  navScroll: {
    paddingBottom: 10,
    overflowY: "auto",
    minHeight: 0,
    flex: 1,
  },

  sectionLabel: {
    padding: "10px 18px 6px",
    marginTop: 6,
    fontSize: 11,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    color: "rgba(148,163,184,0.72)",
    fontWeight: 900,
  },

  divider: {
    margin: "6px 14px 10px",
    height: 1,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.00), rgba(255,255,255,0.10), rgba(255,255,255,0.00))",
  },

  rowLeft: { display: "flex", alignItems: "center", gap: 12 },

  rightMeta: { display: "inline-flex", alignItems: "center", gap: 8 },

  item: (collapsed) => ({
    margin: "3px 10px",
    padding: collapsed ? "10px 10px" : "10px 12px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    textDecoration: "none",
    borderRadius: 14,
    border: "1px solid transparent",
    userSelect: "none",
  }),

  itemText: {
    fontSize: 14,
    fontWeight: 650,
    letterSpacing: "-0.01em",
    color: "rgba(226,232,240,0.92)",
  },

  activePill: (brand) => ({
    marginLeft: "auto",
    fontSize: 10,
    fontWeight: 900,
    letterSpacing: "0.12em",
    padding: "4px 8px",
    borderRadius: 999,
    color: "rgba(255,255,255,0.92)",
    background: `rgba(${brand.primaryRgb},0.16)`,
    border: `1px solid rgba(${brand.primaryRgb},0.28)`,
  }),

  caret: { color: "rgba(148,163,184,0.9)", display: "inline-flex", alignItems: "center" },

  menuHeader: (collapsed) => ({
    width: "calc(100% - 20px)",
    margin: "3px 10px",
    padding: collapsed ? "10px 10px" : "10px 12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 14,
    border: "1px solid transparent",
    background: "transparent",
    color: "rgba(226,232,240,0.90)",
    cursor: "pointer",
    userSelect: "none",
  }),

  submenu: {
    margin: "2px 10px 10px",
    padding: "8px 10px 10px 44px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.06)",
    position: "relative",
  },

  subItem: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "9px 10px",
    borderRadius: 12,
    textDecoration: "none",
    fontSize: 13,
    color: "rgba(203,213,225,0.92)",
    border: "1px solid transparent",
    userSelect: "none",
  },

  badgeBase: {
    fontSize: 11,
    fontWeight: 900,
    padding: "2px 8px",
    borderRadius: 999,
    lineHeight: "18px",
    minWidth: 26,
    textAlign: "center",
  },
  badgeNeutral: {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: "rgba(226,232,240,0.95)",
  },
  badgeInfo: (brand) => ({
    background: `rgba(${brand.primaryRgb},0.16)`,
    border: `1px solid rgba(${brand.primaryRgb},0.28)`,
    color: "rgba(255,255,255,0.92)",
  }),
  badgeWarn: {
    background: "rgba(251,191,36,0.14)",
    border: "1px solid rgba(251,191,36,0.22)",
    color: "rgba(254,243,199,0.95)",
  },

  footer: (collapsed) => ({
    padding: collapsed ? "10px" : "14px",
    borderTop: "1px solid rgba(255,255,255,0.06)",
    background: "rgba(0,0,0,0.08)",
  }),

  logoutBtn: (brand) => ({
    width: "100%",
    padding: "10px 12px",
    borderRadius: 14,
    border: `1px solid rgba(${brand.primaryRgb},0.26)`,
    background: "rgba(220, 38, 38, 0.88)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
    userSelect: "none",
  }),

  footerHint: {
    marginTop: 10,
    fontSize: 12,
    color: "rgba(148,163,184,0.85)",
    textAlign: "center",
  },
};

function css(brand) {
  return `
  .ip-navscroll::-webkit-scrollbar{ width: 10px; }
  .ip-navscroll::-webkit-scrollbar-thumb{
    background: rgba(148,163,184,0.18);
    border-radius: 999px;
    border: 2px solid rgba(0,0,0,0.20);
  }
  .ip-navscroll::-webkit-scrollbar-thumb:hover{ background: rgba(148,163,184,0.28); }

  .ip-iconbtn{
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.04);
    color: rgba(226,232,240,0.92);
    cursor: pointer;
    transition: transform .06s ease, background .15s ease, border .15s ease;
  }
  .ip-iconbtn:hover{
    background: rgba(255,255,255,0.07);
    border-color: rgba(255,255,255,0.14);
  }
  .ip-iconbtn:active{ transform: scale(0.98); }
  .ip-iconbtn:focus{ outline: none; box-shadow: 0 0 0 4px rgba(${brand.primaryRgb},0.20); }

  .ip-iconbox{
    width: 34px; height: 34px; border-radius: 12px;
    display: grid; place-items: center;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.06);
    color: rgba(226,232,240,0.88);
    flex: 0 0 auto;
    transition: background .15s ease, border .15s ease, transform .06s ease;
  }
  .ip-iconbox.is-active{
    background: rgba(${brand.primaryRgb},0.18);
    border-color: rgba(${brand.primaryRgb},0.34);
    color: #fff;
  }

  .ip-item, .ip-menuhead{
    transition: transform .06s ease, background .15s ease, border .15s ease, box-shadow .15s ease;
  }
  .ip-item:hover, .ip-menuhead:hover{
    background: rgba(255,255,255,0.06);
    border-color: rgba(255,255,255,0.08);
    box-shadow: 0 8px 20px rgba(0,0,0,0.18);
    transform: translateY(-1px);
  }
  .ip-item:active, .ip-menuhead:active{ transform: translateY(0px) scale(0.99); }

  .ip-item.is-active, .ip-menuhead.is-active{
    background: rgba(255,255,255,0.07);
    border-color: rgba(255,255,255,0.10);
    box-shadow: inset 2px 0 0 rgba(${brand.primaryRgb},0.95);
  }

  .ip-submenu::before{
    content:"";
    position:absolute;
    left: 26px;
    top: 14px;
    bottom: 14px;
    width: 1px;
    background: rgba(255,255,255,0.10);
    border-radius: 999px;
  }

  .ip-subitem{
    transition: background .15s ease, border .15s ease, transform .06s ease;
  }
  .ip-subitem:hover{
    background: rgba(255,255,255,0.06);
    border-color: rgba(255,255,255,0.08);
    transform: translateY(-1px);
  }
  .ip-subitem.is-active{
    background: rgba(255,255,255,0.08);
    border-color: rgba(255,255,255,0.10);
  }

  .ip-subdot{
    width: 8px; height: 8px; border-radius: 999px;
    background: rgba(148,163,184,0.55);
    box-shadow: 0 0 0 3px rgba(2,6,23,0.35);
    transition: background .15s ease, transform .06s ease;
  }
  .ip-subdot.is-active{
    background: rgba(${brand.primaryRgb},0.95);
    transform: scale(1.05);
  }

  .ip-logout{
    transition: transform .06s ease, filter .15s ease, background .15s ease;
  }
  .ip-logout:hover{ filter: brightness(1.05); }
  .ip-logout:active{ transform: scale(0.99); }
  `;
}
















